import { createEmitter } from '@codesuma/baseline'
import utils from '../utils'
import db from './db'
import supabase from './supabase'
import seed from './seed'
import push from './push'
import helpers from '../helpers'

export interface UserList {
    id: string
    name: string
    created_by: string
    created_at: string
    role: 'owner' | 'member'
    item_count?: number
    done_count?: number // how many items are checked off; local-only (seed/cache), undefined for server lists
    member_count?: number
    shared_by?: string // display name of the owner, for "shared with me" lists
    synced?: boolean // local-only: false for lists created offline, not yet on the server
    color?: number | null // picked palette index; null/absent = hue derived from the id
    icon?: string | null // picked icon key (configs/list-icons); null/absent = guessed from the name
}

let cached: UserList[] = []

// Emitted when the *set* of lists changes (one created, copied or deleted), so
// an already-rendered page doesn't have to be re-entered to notice. Item-level
// edits inside a list are list.ts's business, not this one's.
const emitter = createEmitter()
const on = emitter.on.bind(emitter)

// Pinned lists — a per-device preference (kept out of the shared list rows so
// one member's pin doesn't reorder everyone else's grid), so plain localStorage.
const PIN_KEY = 'papa-pinned'
const pinnedIds = (): string[] => {
    try { return JSON.parse(localStorage.getItem(PIN_KEY) || '[]') || [] } catch { return [] }
}
const isPinned = (id: string): boolean => pinnedIds().includes(id)
const setPinned = (id: string, on: boolean): void => {
    const ids = pinnedIds().filter(x => x !== id)
    if (on) ids.unshift(id) // most recently pinned first
    localStorage.setItem(PIN_KEY, JSON.stringify(ids))
}

// One deterministic order for every code path (instant local load *and* server
// refresh), so the circles never reshuffle when the server data lands: pinned
// lists first (most recently pinned leading), then unsynced (offline-created)
// lists, then newest-first by created_at.
const order = (lists: UserList[]): UserList[] => {
    const pins = pinnedIds()
    const pinRank = (id: string) => { const i = pins.indexOf(id); return i === -1 ? Infinity : i }
    return [...lists].sort((a, b) => {
        const ar = pinRank(a.id)
        const br = pinRank(b.id)
        if (ar !== br) return ar - br
        if (!!a.synced !== !!b.synced) return a.synced ? 1 : -1
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    })
}

// Fetch all lists the user owns or is a member of. Persists the result to
// IndexedDB so lists are viewable offline, and falls back to that cache when the
// server is unreachable. Locally-created lists not yet on the server (created
// offline) are kept and shown newest-first.
const getLists = async (): Promise<UserList[]> => {
    await seed.run() // make sure first-run starter lists exist before we read
    const localUnsynced = (await db.all<UserList>(db.LIST_STORE)).filter(l => !l.synced)

    // Anonymous auth is deferred until the first write, so a fresh visitor has no
    // session and no server lists yet — everything they have is local-only. Skip
    // the doomed /lists/all call (it would 401) and read straight from the cache.
    if (!supabase.auth.isAuthenticated()) {
        cached = order(await db.all<UserList>(db.LIST_STORE))
        return cached
    }

    try {
        const res = await utils.http.get<{ lists: UserList[] }>('/api/v1/lists/all')
        const server = (res.lists || []).map(l => ({ ...l, synced: true }))
        const onServer = new Set(server.map(l => l.id))
        const pending = localUnsynced.filter(l => !onServer.has(l.id))
        await db.clear(db.LIST_STORE)
        for (const l of server) await db.save(db.LIST_STORE, l)
        for (const l of pending) await db.save(db.LIST_STORE, l)
        cached = order([...pending, ...server])
        helpers.syncListColors(cached) // picked colors follow the server rows
    } catch (err) {
        console.warn('Lists offline — loading from local cache:', err)
        cached = order(await db.all<UserList>(db.LIST_STORE))
    }
    return cached
}

// Read whatever lists are cached locally (instant, no network) so the home
// screen can paint immediately; the caller follows up with getLists() to refresh
// from the server. Unsynced (offline-created) lists are kept first to match
// getLists' ordering, so circles don't reshuffle when the server data lands.
const getLocalLists = async (): Promise<UserList[]> => {
    await seed.run()
    cached = order(await db.all<UserList>(db.LIST_STORE))
    return cached
}

// Persist a locally-created list to the server, marking it synced on success.
const pushToServer = async (list: UserList): Promise<boolean> => {
    try {
        await supabase.auth.ensureSession() // mint anon user on first sync (deferred from boot)
        const body: { id: string; name: string; color?: number; icon?: string } = { id: list.id, name: list.name }
        if (typeof list.color === 'number') body.color = list.color // recolored before first sync
        if (list.icon) body.icon = list.icon // picked in the New List wizard
        await utils.http.post('/api/v1/lists/new', body)
        const synced = { ...list, synced: true }
        await db.save(db.LIST_STORE, synced)
        const i = cached.findIndex(l => l.id === list.id)
        if (i !== -1) cached[i] = synced
        return true
    } catch (err) {
        console.warn('List create will retry on reconnect:', list.id, err)
        return false
    }
}

// Create a new (empty) list. The client mints the id (crypto.randomUUID) so the
// list has a stable id/URL from creation. Saved to IndexedDB + memory first
// (works fully offline), then pushed to the server in the background. No icon
// means the circle guesses one from the name.
const createList = async (name: string, icon?: string): Promise<UserList> => {
    const list: UserList = {
        id: crypto.randomUUID(),
        name,
        created_by: '', // server sets the real owner; not needed for the optimistic row
        created_at: new Date().toISOString(),
        role: 'owner',
        item_count: 0,
        member_count: 1,
        synced: false,
        icon: icon || null,
    }
    await db.save(db.LIST_STORE, list)
    cached = [list, ...cached]
    emitter.emit('change', cached)

    pushToServer(list)

    return list
}

// Adopt a list that was written straight to IndexedDB by someone else (a saved
// copy of a public list — see services/public-lists.ts). Mirrors what
// createList does for the in-memory cache, minus the server push: a copy stays
// local until the user's first edit. Without this the row exists on disk but no
// open page knows it appeared.
const registerLocal = (list: UserList): void => {
    if (!cached.some(l => l.id === list.id)) cached = order([list, ...cached])
    emitter.emit('change', cached)
}

// Push a locally-created list (e.g. a starter list) to the server on demand —
// used to sync a list lazily on the user's first edit. No-op if already synced.
const ensureSynced = async (listId: string): Promise<void> => {
    let list = cached.find(l => l.id === listId)
    if (!list) list = (await db.all<UserList>(db.LIST_STORE)).find(l => l.id === listId)
    if (list && !list.synced) await pushToServer(list)
}

// Replay any lists created offline (call when back online).
const syncPending = async (): Promise<void> => {
    const unsynced = (await db.all<UserList>(db.LIST_STORE)).filter(l => !l.synced)
    for (const list of unsynced) {
        const ok = await pushToServer(list)
        if (!ok) break // still offline — stop and try again next time
    }
}

// Rename a list everywhere it lives: local cache + IndexedDB first (instant,
// offline-safe), then the server. A not-yet-synced list is left local-only — its
// eventual create push carries the new name.
const renameList = async (id: string, name: string): Promise<void> => {
    const local = (await db.all<UserList>(db.LIST_STORE)).find(l => l.id === id)
    if (local) await db.save(db.LIST_STORE, { ...local, name })
    const i = cached.findIndex(l => l.id === id)
    if (i !== -1) cached[i] = { ...cached[i], name }

    if (local && local.synced === false) return // not on the server yet
    try {
        await supabase.auth.ensureSession()
        await utils.http.put(`/api/v1/lists/list/${id}`, { name })
    } catch (err) {
        console.warn('Failed to rename list on server:', err)
    }
}

// Set a list's picked palette color everywhere: the helpers override (instant
// paint on every surface), memory cache, IndexedDB, then the server. A
// not-yet-synced list stays local — its eventual create push carries the color.
const setColor = async (id: string, color: number): Promise<void> => {
    helpers.setListColor(id, color)
    const local = (await db.all<UserList>(db.LIST_STORE)).find(l => l.id === id)
    if (local) await db.save(db.LIST_STORE, { ...local, color })
    const i = cached.findIndex(l => l.id === id)
    if (i !== -1) cached[i] = { ...cached[i], color }

    if (local && local.synced === false) return // not on the server yet
    try {
        await supabase.auth.ensureSession()
        await utils.http.put(`/api/v1/lists/list/${id}/color`, { color })
    } catch (err) {
        console.warn('Failed to save list color on server:', err)
    }
}

// Set a list's picked icon: memory cache and IndexedDB, then the server. Same
// shape as setColor. A not-yet-synced list stays local — its create push
// carries the icon.
const setIcon = async (id: string, icon: string): Promise<void> => {
    const local = (await db.all<UserList>(db.LIST_STORE)).find(l => l.id === id)
    if (local) await db.save(db.LIST_STORE, { ...local, icon })
    const i = cached.findIndex(l => l.id === id)
    if (i !== -1) cached[i] = { ...cached[i], icon }

    if (local && local.synced === false) return // not on the server yet
    try {
        await supabase.auth.ensureSession()
        await utils.http.put(`/api/v1/lists/list/${id}/icon`, { icon })
    } catch (err) {
        console.warn('Failed to save list icon on server:', err)
    }
}

// A member recolored a list we can see (delivered by the open list's Realtime
// subscription — see list.ts). Same writes as setColor minus the server call,
// then announce it so the home grid and the open page repaint in place instead
// of waiting for the next full refresh.
const applyRemoteColor = async (id: string, color: number | null): Promise<void> => {
    helpers.setListColor(id, color)
    const i = cached.findIndex(l => l.id === id)
    if (i !== -1) cached[i] = { ...cached[i], color }
    const local = (await db.all<UserList>(db.LIST_STORE)).find(l => l.id === id)
    if (local) await db.save(db.LIST_STORE, { ...local, color })
    emitter.emit('change', cached)
}

// Delete a whole list (owner only)
const deleteList = async (id: string): Promise<void> => {
    await db.delete(db.LIST_STORE, id)
    cached = cached.filter(l => l.id !== id)
    helpers.setListColor(id, null) // drop the picked color with the list
    setPinned(id, false)
    emitter.emit('change', cached)
    try {
        await utils.http.remove(`/api/v1/lists/list/${id}`, {})
    } catch (err) {
        console.warn('Failed to delete list on server:', err)
    }
}

// Sync lookup into the last-fetched lists (used for the detail page title)
const getCachedName = (id: string): string | null => {
    return cached.find(l => l.id === id)?.name || null
}

// The latest cached row for a list (used by the home page to refresh a circle's
// counts when it returns to a list it already drew a circle for).
const getCached = (id: string): UserList | null => cached.find(l => l.id === id) || null

// Keep a list's item/done counts in step with edits made on the list page, so the
// home circle shows fresh "done/total" progress. Updates the in-memory cache (read
// synchronously, so a return-to-home reflects it immediately) and the IndexedDB row.
const updateCounts = async (id: string, item_count: number, done_count: number): Promise<void> => {
    const i = cached.findIndex(l => l.id === id)
    if (i !== -1) cached[i] = { ...cached[i], item_count, done_count }
    const local = (await db.all<UserList>(db.LIST_STORE)).find(l => l.id === id)
    if (local) await db.save(db.LIST_STORE, { ...local, item_count, done_count })
}

export interface PendingInvite {
    id: string
    list_id: string
    list_name: string | null
    inviter_handle: string | null
    inviter_name: string | null
    created_at: string
}

// Share requests awaiting this user's accept/reject.
const getPendingInvites = async (): Promise<PendingInvite[]> => {
    try {
        const res = await utils.http.get<{ invites: PendingInvite[] }>('/api/v1/lists/invites')
        return res.invites || []
    } catch (err) {
        console.warn('Failed to load invites:', err)
        return []
    }
}

// Accept (join the list) or reject (discard) a pending invite.
const respondToInvite = async (memberId: string, accept: boolean): Promise<void> => {
    await utils.http.post(`/api/v1/lists/invites/${memberId}/respond`, { accept })
    // You just joined a shared list — a natural moment to ask for notifications
    if (accept) push.enable()
}

export default {
    on,
    getLists,
    getLocalLists,
    createList,
    registerLocal,
    ensureSynced,
    syncPending,
    renameList,
    setColor,
    setIcon,
    applyRemoteColor,
    isPinned,
    setPinned,
    deleteList,
    getCachedName,
    getCached,
    updateCounts,
    getPendingInvites,
    respondToInvite,
}
